import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

import {
  Button,
  ButtonsWrapper,
  Form,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  SelectInput,
  Separator,
} from '@thinknet-gmbh/platform-common-ui';

import { useRouteQuery } from 'src/common/helpers';
import { useGetAllInterviewTypes } from 'src/hooks/interview-types/get-interview-types.hook';
import { useUpdateAppraisalInterview } from 'src/hooks/appraisal-interview/update-appraisal-interview.hook';

import { DeleteContainer } from './employee-interview-details-edit.styles';
import { IAppraisalEditProps } from './employee-interview-details-edit.interfaces';
import { EmployeeInterviewEditFormElements } from './components/employee-interview-edit-form-elements';

const EmployeeInterviewDetailsEdit = ({ data, onClose, isAddedInterview }: IAppraisalEditProps) => {
  const { t } = useTranslation(['common', 'employee']);
  const [interviewTypesIds, setInterviewTypesIds] = useState<string[]>([]);
  const [form] = Form.useForm();
  const ref = useRef<HTMLDivElement | null>(null);
  const { interviewTypes } = useGetAllInterviewTypes();
  const { updateInterview } = useUpdateAppraisalInterview({ onSuccess: onClose });
  const query = useRouteQuery();
  const memberId = query.get('id') || '';

  useEffect(() => {
    if (isAddedInterview) {
      ref.current?.click();
    }
  }, [isAddedInterview]);

  const removeInterviewType = (key: number) => {
    const newTypes = [...interviewTypesIds];

    newTypes.splice(key, 1);

    setInterviewTypesIds(newTypes);
  };

  const onFinish = (values: any) => {
    const { interviews, ...interviewToUpdate } = values;

    updateInterview({
      variables: {
        appraisalInterviewId: data._id,
        payload: {
          ...interviewToUpdate,
          ...(interviews && {
            interviewsToCreate: interviews.map((item: any) => ({
              ...item,
              memberId,
            })),
          }),
        },
      },
    });
  };

  return (
    <Form
      layout="vertical"
      initialValues={{
        ...data,
        setTo: moment(data.setTo),
      }}
      onFinish={onFinish}
      form={form}
    >
      <EmployeeInterviewEditFormElements initialValues={data} interviewTypesList={interviewTypes} />
      <Form.List name="interviews">
        {(fields, { remove, add }) => (
          <div>
            <div ref={ref} onClick={() => add({ status: data.status })} />
            {fields.map(field => (
              <div key={field.key}>
                {interviewTypesIds[field.name] ? (
                  <EmployeeInterviewEditFormElements
                    form={form}
                    interviewTypesList={interviewTypes}
                    field={field}
                    remove={() => {
                      remove(field.name);
                      removeInterviewType(field.name);
                    }}
                    interviewTypeId={interviewTypesIds[field.name]}
                  />
                ) : (
                  <>
                    <Separator />
                    <DeleteContainer>
                      <Form.Item
                        name="interviewTypeId"
                        label={t('employee:employeeInterview.labels.interviewType')}
                        rules={[
                          {
                            required: true,
                            message: t(
                              'employee:employeeInterview.validation.interviewTypeRequired'
                            ),
                          },
                        ]}
                      >
                        <SelectInput
                          onChange={value => setInterviewTypesIds(oldValue => [...oldValue, value])}
                          placeholder={t('employee:employeeInterview.labels.selectInterviewType')}
                        >
                          {interviewTypes.map(({ _id, name }: any) => (
                            <SelectInput.Option value={_id} key={_id}>
                              {name}
                            </SelectInput.Option>
                          ))}
                        </SelectInput>
                      </Form.Item>
                      <Icon
                        icon={ICONS.trash}
                        color={PLATFORM_THEME_COLORS.tone.red}
                        rightMargin={false}
                        size={22}
                        onClick={() => {
                          remove(field.name);
                          removeInterviewType(field.name);
                        }}
                      />
                    </DeleteContainer>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Form.List>

      <Separator />
      <ButtonsWrapper marginTop={16}>
        <Button type="primary" htmlType="submit">
          {t('common:buttons.save')}
        </Button>
        <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default EmployeeInterviewDetailsEdit;
